export interface UploadBatch { file: Blob; originalRows: number[]; }

export async function createUploadBatches(file: File, records: Array<{ rows: number[] }>): Promise<UploadBatch[]> {
  const XLSX = await import("xlsx");
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true, sheets: ["Data"] });
  const sheet = workbook.Sheets.Data;
  if (!sheet) throw new Error("The workbook has no Data sheet.");
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: true, blankrows: true });
  const batches: UploadBatch[] = [];
  for (let index = 0; index < records.length; index += 10) {
    const originalRows = records.slice(index, index + 10).flatMap((record) => record.rows);
    const data = [rows[0], ...originalRows.map((number) => {
      if (!rows[number - 1]) throw new Error(`Workbook row ${number} is missing.`);
      return rows[number - 1];
    })];
    const batch = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(batch, XLSX.utils.aoa_to_sheet(data, { cellDates: true }), "Data");
    batches.push({ file: new Blob([XLSX.write(batch, { type: "array", bookType: "xlsx", cellDates: true })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), originalRows });
  }
  return batches;
}

export const uploadInstructions = {
  categories: {
    required: "name",
    rules: "One category per row. Names must be unique and 2-100 characters. displayOrder is a whole number of zero or greater; icon and color are optional.",
  },
  "spare-parts": {
    required: "name, categoryCode, reorderLevel, maxQuantityPerServiceIssue",
    rules: "Create categories first. Copy categoryCode from the template references. Spare codes and internal part numbers are generated automatically. Each spare name must be unique within its category for bulk imports. reorderLevel is required and must be a whole number of zero or greater. maxQuantityPerServiceIssue is required and must be a positive whole number. Other stock thresholds and warranty months must be whole numbers of zero or greater when supplied. This creates spare master records, not hub stock.",
  },
  suppliers: {
    required: "name, contactPerson, phone",
    rules: "One supplier per row. Supplier names must be unique. Store phone numbers and pincode as text to preserve leading zeros. Phone must contain 10-15 characters; email must be valid when supplied.",
  },
  "purchase-bills": {
    required: "supplierCode, invoiceNumber, invoiceDate, purchaseDate, hubCode, sparePartCode, quantity, unitPrice, currencyAmount",
    rules: "Create suppliers and spares first, then copy their codes and an allowed hubCode from the reference sheets. One row per spare: repeat identical bill fields on every item row of the same invoice. Do not repeat a spare within that invoice. quantity is a positive whole number; unitPrice and otherCharges cannot be negative. Repeat the entire bill's currencyAmount on each row; it must match the item totals plus otherCharges. Bills start as drafts; receive them normally to add hub inventory. The 500-row limit counts item rows, not only invoice headers.",
  },
} as const;
